import type { CardDef } from "@reagent/sim";
import {
  COLOUR_CRITICAL_TEMP,
  MELT_BUFFER,
  MELT_CRITICAL_TEMP,
  MT_CEMENT_BUFFER,
  MT_SMELTER_BUFFER,
  MT_SMELTER_CRITICAL_TEMP,
  PF_STEEP_BUFFER,
  PF_STEEP_CRITICAL_TEMP,
} from "./stages/era1/machines";

/**
 * The card catalogue: 47 cards across the categories of `02-cards-and-economy.md` §2.
 *
 * Costs are in kW against the turn's Power Budget (`02-cards-and-economy.md`
 * §1), so the shape of a turn is roughly "one expensive thing, or three cheap
 * ones". Every hazard mitigation named in `03-hazards.md` §2 has a card here,
 * because a hazard the rules say is survivable
 * has to actually be survivable.
 */

/** A reactor card bound to one recipe (spec 0068). */
const bound = (
  id: string,
  name: string,
  costKw: number,
  recipeId: string,
  config: { readonly criticalTemp?: number; readonly bufferCapacity?: number } = {},
): CardDef => ({
  id,
  name,
  category: "hardware",
  costKw,
  deploysNode: "reactor",
  deploysNodeConfig: { ...config, recipeIds: [recipeId] },
  injectsCards: { "card-flash-freeze": 1 },
});

const cards: CardDef[] = [
  // ---- Hardware: pipes -------------------------------------------------
  {
    id: "card-pipe-glass",
    name: "Glass Pipe",
    category: "hardware",
    costKw: 1,
    deploysPipe: "glass",
  },
  {
    id: "card-pipe-ptfe",
    name: "PTFE-Lined Pipe",
    category: "hardware",
    costKw: 2,
    deploysPipe: "ptfeLined",
  },
  {
    id: "card-pipe-hastelloy",
    name: "Hastelloy Pipe",
    category: "hardware",
    costKw: 3,
    deploysPipe: "hastelloy",
  },
  // Spec 0017 open question 2: setup-phase emergency hardware. Ordinary
  // hardware cards played through the existing playCard/connectPipe commands
  // during Play — nothing new needed there. Not dealt into any shipped
  // stage's startingDeck yet; catalogue-only until spec 0037 places them.
  {
    id: "card-emergency-bypass",
    name: "Emergency Bypass Pipe",
    category: "hardware",
    costKw: 2,
    deploysPipe: "glass",
  },

  // ---- Hardware: machines ----------------------------------------------
  {
    id: "card-mixer",
    name: "Mixer Node",
    category: "hardware",
    costKw: 2,
    deploysNode: "reactor",
    injectsCards: { "card-flash-freeze": 1 },
  },
  {
    id: "card-mixer-heavy",
    name: "Heavy-Duty Mixer",
    category: "hardware",
    costKw: 4,
    deploysNode: "reactor",
    // Runs hotter before it complains and holds more while it waits.
    deploysNodeConfig: { criticalTemp: 8, bufferCapacity: 4 },
    injectsCards: { "card-flash-freeze": 1 },
  },
  // Spec 0068: one reactor card per Era 1 line, pre-bound to its machine's recipe
  // (a default that an install's own recipeIds replaces). Config reads the same
  // constants as the module's pre-placed NodeDef. costKw is card-mixer's 2, or
  // card-mixer-heavy's 4 where the card carries bufferCapacity 4. PLACEHOLDER names.
  bound("card-tp-maceration-vat", "Maceration Vat", 2, "tp-maceration"),
  bound("card-tp-dip-vat", "Dip Vat", 2, "tp-dip"),
  bound("card-gf-blend", "Fire Blender", 2, "gf-blend-fire"),
  bound("card-gf-siphon", "Sea-Fire Siphon", 2, "gf-siphon-sea"),
  bound("card-gl-melt", "Glass Melt Furnace", 4, "gl-melt", { criticalTemp: MELT_CRITICAL_TEMP, bufferCapacity: MELT_BUFFER }),
  bound("card-gl-colour", "Colour Furnace", 2, "gl-colour", { criticalTemp: COLOUR_CRITICAL_TEMP }),
  bound("card-mt-smelter", "Smelter", 4, "mt-smelt", { criticalTemp: MT_SMELTER_CRITICAL_TEMP, bufferCapacity: MT_SMELTER_BUFFER }),
  bound("card-mt-cementation", "Cementation Reactor", 4, "mt-cementation", { bufferCapacity: MT_CEMENT_BUFFER }),
  bound("card-pf-steep", "Warm Steep", 4, "pf-steep", { criticalTemp: PF_STEEP_CRITICAL_TEMP, bufferCapacity: PF_STEEP_BUFFER }),
  bound("card-pf-salve", "Salve Reactor", 2, "pf-salve"),
  {
    id: "card-splitter",
    name: "Splitter",
    category: "hardware",
    costKw: 2,
    deploysNode: "flowMover",
    injectsCards: { "card-solvent-flush": 1 },
  },
  {
    id: "card-pump",
    name: "Pump",
    category: "hardware",
    costKw: 3,
    deploysNode: "flowMover",
    deploysNodeConfig: { pumpOverride: 1 },
    injectsCards: { "card-solvent-flush": 1 },
  },
  {
    id: "card-catalytic-filter",
    name: "Catalytic Filter",
    category: "hardware",
    costKw: 3,
    deploysNode: "catalyticBed",
    injectsCards: { "card-catalyst-recharge": 1 },
  },
  {
    id: "card-storage-tank",
    name: "Storage Tank",
    category: "hardware",
    costKw: 2,
    deploysNode: "flowBuffer",
    injectsCards: { "card-emergency-flush": 1 },
  },
  {
    id: "card-surge-tank",
    name: "Surge Tank",
    category: "hardware",
    costKw: 3,
    deploysNode: "flowBuffer",
    deploysNodeConfig: { bufferCapacity: 8 },
    injectsCards: { "card-emergency-flush": 1 },
  },
  {
    id: "card-product-hopper",
    name: "Product Hopper",
    category: "hardware",
    costKw: 2,
    deploysNode: "productHopper",
    deploysNodeConfig: { bufferCapacity: 99 },
    injectsCards: { "card-emergency-flush": 1 },
  },
  {
    id: "card-vent",
    name: "Vent",
    category: "hardware",
    costKw: 2,
    deploysNode: "vent",
    deploysNodeConfig: { bufferCapacity: 99 },
  },
  {
    id: "card-phase-converter",
    name: "Phase Converter",
    category: "hardware",
    costKw: 3,
    deploysNode: "phaseConverter",
    injectsCards: { "card-nitrogen-purge": 1 },
  },
  {
    id: "card-incubator",
    name: "Incubator",
    category: "hardware",
    costKw: 3,
    deploysNode: "incubator",
    injectsCards: { "card-solvent-flush": 1 },
  },
  // Spec 0022: two thermal regulator cards, a chiller and a heater with
  // placeholder ±30°C (pending playtest) — see the resolution of Open
  // Question 1 in the spec. No injectsCards: 02-cards-and-economy.md §2.1's
  // Bound Hardware table has no operational card entry for Thermal Regulators
  // yet (marked as "no device exists yet"). Do not add one on speculation.
  {
    id: "card-chiller",
    name: "Chiller",
    category: "hardware",
    costKw: 2,
    deploysNode: "thermalRegulator",
    deploysNodeConfig: { deltaTemp: -30 },
  },
  {
    id: "card-heater",
    name: "Heater",
    category: "hardware",
    costKw: 2,
    deploysNode: "thermalRegulator",
    deploysNodeConfig: { deltaTemp: 30 },
  },
  // Spec 0017 open question 2: setup-phase emergency hardware, see the bypass
  // pipe above for the same note.
  {
    id: "card-emergency-buffer",
    name: "Emergency Buffer",
    category: "hardware",
    costKw: 3,
    deploysNode: "flowBuffer",
    injectsCards: { "card-emergency-flush": 1 },
  },

  // ---- Attachments ------------------------------------------------------
  {
    id: "card-cooling-jacket",
    name: "Cooling Jacket",
    category: "attachment",
    costKw: 1,
    attachment: { kind: "coolingJacket", heatShedPerTick: 1 },
  },
  {
    id: "card-cooling-jacket-twin",
    name: "Twin Cooling Jacket",
    category: "attachment",
    costKw: 3,
    attachment: { kind: "coolingJacket", heatShedPerTick: 2 },
  },
  {
    id: "card-ptfe-lining",
    name: "PTFE Lining",
    category: "attachment",
    costKw: 2,
    attachment: { kind: "ptfeLining" },
  },
  {
    id: "card-burst-disk",
    name: "Burst Disk",
    category: "attachment",
    costKw: 1,
    attachment: { kind: "burstDisk", extraPressureTolerance: 2 },
  },
  {
    id: "card-guard-bed",
    name: "Guard Bed",
    category: "attachment",
    costKw: 2,
    attachment: { kind: "guardBed", charges: 4 },
  },
  {
    id: "card-polishing-filter",
    name: "Polishing Filter",
    category: "attachment",
    costKw: 3,
    attachment: { kind: "guardBed", charges: 8 },
  },
  {
    id: "card-insulation",
    name: "Thermal Insulation",
    category: "attachment",
    costKw: 2,
    attachment: { kind: "insulation", extraCriticalTemp: 3 },
  },
  {
    id: "card-surge-drum",
    name: "Surge Drum",
    category: "attachment",
    costKw: 2,
    attachment: { kind: "surgeDrum", extraBufferCapacity: 3 },
  },
  {
    id: "card-lid",
    name: "Lid",
    category: "attachment",
    costKw: 2,
    attachment: { kind: "lid" },
    attachesToNodeKind: "wasteBuyer",
  },

  // ---- Directives -------------------------------------------------------
  {
    id: "card-flash-freeze",
    name: "Flash Freeze",
    category: "directive",
    costKw: 2,
    directive: { kind: "flashFreeze" },
  },
  {
    id: "card-solvent-flush",
    name: "Solvent Flush",
    category: "directive",
    costKw: 1,
    directive: { kind: "solventFlush" },
  },
  {
    id: "card-nitrogen-purge",
    name: "Nitrogen Purge",
    category: "directive",
    costKw: 1,
    directive: { kind: "nitrogenPurge" },
  },
  {
    id: "card-emergency-flush",
    name: "Emergency Flush",
    category: "directive",
    costKw: 2,
    directive: { kind: "emergencyFlush" },
  },
  {
    id: "card-patch-weld",
    name: "Patch Weld",
    category: "directive",
    costKw: 2,
    directive: { kind: "patchWeld", hp: 60 },
  },
  {
    id: "card-catalyst-recharge",
    name: "Catalyst Recharge",
    category: "directive",
    costKw: 3,
    directive: { kind: "catalystRecharge" },
  },
  // Spec 0033: pest infestation mitigation — clears an infested node (costKw
  // placeholder, needs playtest confirmation). Never appears in starter decks or
  // stage setupCatalogue; obtained from the between-stages shop only.
  {
    id: "card-fumigate",
    name: "Fumigate",
    category: "directive",
    costKw: 2,
    directive: { kind: "fumigate" },
  },
  // Spec 0067 OQ11: the kW route to cleaning a contaminated tile, so it does not
  // depend on the Chemical Spill card arriving by draw. costKw matches that
  // card's clear (placeholder, needs playtest). Between-stages shop only, like Fumigate.
  {
    id: "card-scrub",
    name: "Scrub",
    category: "directive",
    costKw: 2,
    directive: { kind: "scrubTile" },
  },
  // Spec 0035: signature directives that target nodes. Stir the Vat resets an
  // incubator's residence clock; Low Fire caps a reactor's firing multiples.
  // Both are general catalogue cards for between-stages shop offer.
  {
    id: "card-stir-the-vat",
    name: "Stir the Vat",
    category: "directive",
    costKw: 1,
    directive: { kind: "stirVat" },
  },
  {
    id: "card-low-fire",
    name: "Low Fire",
    category: "directive",
    costKw: 2,
    directive: { kind: "lowFire", maxMultiple: 1 },
  },
  // Spec 0020 OQ2: recategorised to metaUtility. The effect is still a
  // directive payload — metaUtility reuses that field rather than inventing
  // its own.
  {
    id: "card-overtime",
    name: "Overtime Shift",
    category: "metaUtility",
    costKw: 1,
    directive: { kind: "overtime", extraKw: 3 },
  },
  {
    id: "card-rush-order",
    name: "Rush Order",
    category: "metaUtility",
    costKw: 1,
    directive: { kind: "rushOrder", cards: 2 },
  },

  // ---- Hazards ----------------------------------------------------------
  // These are what neglect shuffles into the deck (`02-cards-and-economy.md`
  // §2.5), one per `03-hazards.md` §4 row, each
  // carrying that row's own differentiated deck impact rather than a shared
  // end-of-Play bulk-damage counter (spec 0007). `isPlayable: false` cards cost
  // nothing, since a card that can never be played is never charged for.

  // Exothermic Runaway — renamed from the old bulk-heat hazard card.
  {
    id: "card-thermal-shock",
    name: "Thermal Shock",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "powerPenalty", kw: 1 },
    isPlayable: false,
  },
  // Line Over-Pressure (new "burst" reason) — no predecessor id; a burst
  // injected nothing before this spec.
  {
    id: "card-line-leak",
    name: "Line Leak",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "drawPenalty", cards: 1 },
    isPlayable: false,
  },
  // Material Corrosion — renamed from the old bulk-corrosion hazard card.
  {
    id: "card-corrosive-pitting",
    name: "Corrosive Pitting",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "directiveSurcharge", kw: 1 },
    isPlayable: false,
  },
  // Catalyst Poisoning — renamed from the old bulk-poison hazard card.
  {
    id: "card-catalyst-deactivation",
    name: "Catalyst Deactivation",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "maintenanceFee", credits: 2 },
    isPlayable: false,
  },
  // Sludge Stagnation — renamed from the old bulk-sludge hazard card.
  {
    id: "card-clogged-line",
    name: "Clogged Line",
    category: "hazard",
    costKw: 1,
    hazard: { kind: "tileLock" },
    clearsOnPlay: true,
  },
  // Chemical Spill (§3.1, spec 0005) — no predecessor id.
  {
    id: "card-chemical-spill",
    name: "Chemical Hazard Spill",
    category: "hazard",
    costKw: 2,
    hazard: { kind: "handSlotLock" },
    clearsOnPlay: true,
  },
  // Electrical Outage (§3.2, spec 0006) — no predecessor id.
  {
    id: "card-power-outage",
    name: "Power Grid Outage",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "powerDrain" },
    isPlayable: false,
  },
  // Pest Infestation (§3.3, spec 0033) — a dead draw; text describes the slowdown.
  {
    id: "card-rodent-infestation",
    name: "Rodent Infestation",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "infestation" },
    isPlayable: false,
  },
  // Structural Disaster (§3.4, spec 0006) — no predecessor id.
  {
    id: "card-structural-shear",
    name: "Structural Shear",
    category: "hazard",
    costKw: 0,
    hazard: { kind: "pipeStress", percentOfMaxHp: 10 },
    isPlayable: false,
  },

  // ---- Facility mitigations (spec 0028) ---------------------------------
  // Facility hazard answers per §3 consequences (Resolved Questions 1–2):
  // one Phase 1 install per consequence, ₵ 0 kW (bought with Phase 1 Credits
  // in setupCatalogue), and stage-scoped duration. See
  // docs/specs/implemented/0028-facility-mitigation-cards.md for effects.
  {
    id: "card-hazmat-absorbent",
    name: "Hazmat Absorbent",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "hazmatAbsorbent" },
  },
  {
    id: "card-surge-protector",
    name: "Surge Protector",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "surgeProtector" },
  },
  {
    id: "card-emergency-power-cell",
    name: "Emergency Power Cell",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "emergencyPower", kw: 2 },
  },
  {
    id: "card-facility-mousetrap",
    name: "Facility Mousetrap",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "pestControl" },
  },
  {
    id: "card-hvac-chillers",
    name: "HVAC Chillers",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "hvacChiller" },
  },
  {
    id: "card-seismic-mounts",
    name: "Seismic Isolation Mounts",
    category: "metaUtility",
    costKw: 0,
    facility: { kind: "seismicMounts" },
  },
];

export const ALL_CARDS: readonly CardDef[] = cards;
